import { eachDayOfInterval } from "date-fns";
import { confirm } from "devextreme/ui/dialog";
import notify from "../../../lib/notify";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  cancelAbsence2,
  deleteOwnAbsence2,
  sendAbsence2,
} from "../../../api/absence2";
import { useAuth } from "../../../contexts/auth";
import {
  EmploymentIndex,
  employmentKey,
  isDayWithinEmployment,
} from "../../../lib/employment";
import { ABSENCE_STATUS } from "../constants";
import type { CEID } from "../../../types";
import type {
  AbsenceNameOption2,
  HolidayDataMap2,
  UserForSelect2,
} from "../types";

interface UseAbsenceActionsProps {
  currentMonth: Date;
  viewMode: string;
  isForeman: boolean;
  isHR: boolean;
  holidayData: HolidayDataMap2;
  users: UserForSelect2[];
  setUsers: (fn: (prev: UserForSelect2[]) => UserForSelect2[]) => void;
  names: AbsenceNameOption2[];
  fetchAbsences: (
    year: number,
    month: number,
    empIds: number[],
    compIds: number[],
  ) => Promise<void>;
  fetchAllEmployeesData: (date: Date) => Promise<void>;
  employmentIndex: EmploymentIndex;
}

export function useAbsenceActions({
  currentMonth,
  viewMode,
  isForeman,
  isHR,
  holidayData,
  users,
  setUsers,
  names,
  fetchAbsences,
  fetchAllEmployeesData,
  employmentIndex,
}: UseAbsenceActionsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [popupVisible, setPopupVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>();
  const [absenceName, setAbsenceName] = useState("");
  const [notes, setNotes] = useState("");
  const [skipDaysOff, setSkipDaysOff] = useState(true);
  const [leaveForAssignment, setLeaveForAssignment] = useState<number>(0);
  const [userSend, setUserSend] = useState<number[]>([]);
  const [entriesEmployeeId, setEntriesEmployeeId] = useState<number>();
  const [onlyWeekendDays, setOnlyWeekendDays] = useState(false);
  const [cancelAbsenceData, setCancelAbsenceData] = useState<number>();
  const [cancelRequestPending, setCancelRequestPending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editableAbsenceId, setEditableAbsenceId] = useState<
    number | undefined
  >();
  const [hourFrom, setHourFrom] = useState<string | null>(null);
  const [hourTo, setHourTo] = useState<string | null>(null);
  const [addByHour, setAddByHour] = useState(false);

  const canBeHourly =
    names.find((n) => n.name === absenceName)?.rozliczDni === 1;
  const isHourlyAbsence = canBeHourly && addByHour;

  // Reset checkbox gdy typ nie obsługuje godzin
  useEffect(() => {
    if (!canBeHourly) {
      setAddByHour(false);
    }
  }, [canBeHourly]);

  // Wymuś ten sam dzień dla urlopu godzinowego
  useEffect(() => {
    if (isHourlyAbsence && selectedDate) {
      setSelectedEndDate(selectedDate);
    }
    if (!isHourlyAbsence) {
      setHourFrom(null);
      setHourTo(null);
    }
  }, [isHourlyAbsence, selectedDate]);

  const lastCellClickTime = useRef(0);

  // Weekend detection
  useEffect(() => {
    if (selectedDate && selectedEndDate) {
      const dates = eachDayOfInterval({
        start: selectedDate,
        end: selectedEndDate,
      });
      let weekendDays = 0;
      for (const date of dates) {
        if (date.getDay() === 6 || date.getDay() === 0) weekendDays++;
      }
      setOnlyWeekendDays(skipDaysOff && dates.length === weekendDays);
    }
  }, [selectedDate, selectedEndDate, skipDaysOff]);

  const handleDateClick = useCallback(
    (date: Date, employeeId: number, companyId: number) => {
      if (isForeman && viewMode === "AllEmployees") return;

      const formattedDate = date.toLocaleDateString("en-CA");
      const currentAbsence = holidayData[employeeId]?.find((a) => {
        if (Number(a.confirmed) === ABSENCE_STATUS.REJECTED) return false;
        const from = a.fromDate.split("T")[0];
        const to = a.toDate.split("T")[0];
        return formattedDate >= from && formattedDate <= to;
      });

      // Already cancelled and waiting for Optima removal
      if (
        currentAbsence?.confirmed === ABSENCE_STATUS.CANCELLED &&
        currentAbsence?.synch === 0
      ) {
        notify(
          t(
            "holidayPopup.selectedAttendanceHasAlreadyBeenCanceledAndIsWaitingToBeRemovedFromOptima",
          ),
          "warning",
        );
        return;
      }

      // Locked (synced with Optima)
      const isLocked =
        currentAbsence?.confirmed === ABSENCE_STATUS.HR_CONFIRMED &&
        currentAbsence?.synch === 1;
      if (isLocked && !isHR) {
        notify(t("holidayPopup.popupBlock"), "error");
        return;
      }

      // From Optima - cannot edit
      const isFromOptima = holidayData[employeeId]?.some((a) => {
        const from = a.fromDate.split("T")[0];
        const to = a.toDate.split("T")[0];
        return (
          formattedDate >= from &&
          formattedDate <= to &&
          Number(a.transferred) === 0
        );
      });

      if (isFromOptima) {
        notify(t("holiday.Edit"), "warning");
        return;
      }

      // AllEmployees: HR_CONFIRMED (synch=0) — delete blocked, prevent duplicate POST
      if (
        viewMode === "AllEmployees" &&
        currentAbsence?.confirmed === ABSENCE_STATUS.HR_CONFIRMED &&
        !isLocked
      ) {
        notify(t("holidayPopup.popupBlock"), "error");
        return;
      }

      // Blokada tworzenia urlopu w dniu poza okresem zatrudnienia (umowa do X / od Y).
      // Istniejące urlopy można nadal otworzyć (edycja/usunięcie) - blokujemy tylko nowe.
      if (
        !currentAbsence &&
        !isDayWithinEmployment(
          employmentIndex.get(employmentKey(companyId, employeeId)),
          date,
        )
      ) {
        notify(t("holidayPopup.outsideEmployment"), "warning");
        return;
      }

      setSelectedDate(date);
      setSelectedEndDate(date);
      setEntriesEmployeeId(employeeId);

      // Set user for sending
      const userName = users.find(
        (u) => u.id === employeeId && u.companyId === companyId,
      );
      if (userName) {
        setUserSend((prev) =>
          prev.includes(employeeId) ? prev : [...prev, employeeId],
        );
        if (!users.includes(userName)) {
          setUsers((prev) => [...prev, userName]);
        }
      } else {
        setUserSend([]);
      }

      // Pre-fill form if editing existing absence
      if (currentAbsence) {
        setNotes(currentAbsence.note || "");
        setAbsenceName(currentAbsence.name);
        if (isLocked) setCancelAbsenceData(currentAbsence.id);

        // Edycja własnego niezatwierdzonego urlopu
        const isOwnUnconfirmed =
          currentAbsence.confirmed === ABSENCE_STATUS.UNCONFIRMED &&
          employeeId === user?.employeeId &&
          companyId === user?.companyId;

        // Kierownik/manager/HR/admin: usuwanie urlopu podwładnego dopóki nie zatwierdzono przez kadry (flag != 2)
        const canManagerDelete =
          viewMode === "AllEmployees" &&
          !isOwnUnconfirmed &&
          !isLocked &&
          currentAbsence.confirmed !== ABSENCE_STATUS.HR_CONFIRMED &&
          currentAbsence.confirmed !== ABSENCE_STATUS.CANCELLED;

        if (isOwnUnconfirmed || canManagerDelete) {
          // Odbuduj oryginalny zakres dat (rekordy z DB są splitowane per dzień)
          const sameAbsenceParts =
            holidayData[employeeId]?.filter(
              (a) => a.id === currentAbsence.id,
            ) ?? [];
          const fromIso = sameAbsenceParts.reduce(
            (min, a) => (a.fromDate < min ? a.fromDate : min),
            currentAbsence.fromDate,
          );
          const toIso = sameAbsenceParts.reduce(
            (max, a) => (a.toDate > max ? a.toDate : max),
            currentAbsence.toDate,
          );
          setSelectedDate(new Date(fromIso));
          setSelectedEndDate(new Date(toIso));
          setSkipDaysOff(currentAbsence.skipDaysOff);
          setLeaveForAssignment(currentAbsence.leaveForAssignment ?? 0);
          setEditableAbsenceId(currentAbsence.id);
        } else {
          setEditableAbsenceId(undefined);
        }
      } else {
        setNotes("");
        setAbsenceName("");
        setEditableAbsenceId(undefined);
      }

      setHourFrom(null);
      setHourTo(null);
      setAddByHour(false);

      setPopupVisible(true);
    },
    [
      isForeman,
      viewMode,
      isHR,
      holidayData,
      users,
      setUsers,
      t,
      user,
      employmentIndex,
    ],
  );

  const handleSubmitAbsence = useCallback(
    async (employeeId: number) => {
      if (!selectedDate || !selectedEndDate) {
        notify(t("Please select a date."), "error");
        return;
      }
      if (selectedDate > selectedEndDate) {
        notify(
          t("The start date cannot be greater than or equal to the end date."),
          "error",
        );
        return;
      }

      // Walidacja: cały zakres złożony wyłącznie z weekendów + skipDaysOff
      if (skipDaysOff) {
        const allDates: Date[] = [];
        const current = new Date(selectedDate);
        while (current <= selectedEndDate) {
          allDates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        const hasWorkingDay = allDates.some(
          (d) => d.getDay() !== 0 && d.getDay() !== 6,
        );
        if (!hasWorkingDay) {
          notify(`${t("Error:") || t("Unknown error")}`, "error");
          return;
        }
      }

      if (isHourlyAbsence) {
        if (!hourFrom || !hourTo) {
          notify(t("Please select a date."), "error");
          return;
        }
        if (hourFrom >= hourTo) {
          notify(
            t(
              "The start date cannot be greater than or equal to the end date.",
            ),
            "error",
          );
          return;
        }
      }

      setIsSubmitting(true);
      try {
        // Budujemy listę par {companyId, employeeId} z poprawnym przypisaniem
        // spółki do każdego pracownika. Wcześniej companyId i employeeId były
        // trzymane w dwóch osobnych listach, które potrafiły rozjechać się
        // indeksami (np. dwóch pracowników z tej samej spółki) i powodowały
        // naruszenie klucza obcego FK_Absences_AspNetUsers_CompanyId_EmployeeId.
        const employees: CEID[] =
          userSend.length > 0
            ? userSend.map((employeeId) => {
                const matched = users.find((u) => u.id === employeeId);
                return {
                  employeeId,
                  companyId: matched?.companyId ?? user?.companyId ?? 0,
                };
              })
            : [
                {
                  employeeId: user?.employeeId ?? 0,
                  companyId: user?.companyId ?? 0,
                },
              ];

        // Wysyłamy jeden rekord na pełny zakres - synchronizator z Optimą
        // sam rozbije zakres po dniach roboczych dzięki fladze SkipDaysOff.
        const response = await sendAbsence2(
          user?.employeeId || 0,
          user?.companyId || 0,
          selectedDate,
          selectedEndDate,
          absenceName,
          skipDaysOff,
          leaveForAssignment ? 1 : 0,
          notes,
          employees,
          undefined,
          isHourlyAbsence ? hourFrom : null,
          isHourlyAbsence ? hourTo : null,
        );

        if (!response.isOk) {
          notify(
            `${t("Error:")} ${response.error || t("Unknown error")}`,
            "error",
          );
        } else {
          notify({
            message: t("Holiday has been successfully saved."),
            type: "success",
            displayTime: 5000,
            position: {
              my: "bottom center",
              at: "bottom center",
              of: window,
              offset: "0 -80",
            },
          });
          if (response.data?.warning) {
            notify({
              message: response.data.warning,
              type: "warning",
              displayTime: 8000,
              position: {
                my: "bottom center",
                at: "bottom center",
                of: window,
                offset: "0 -20",
              },
            });
          }
        }

        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        await fetchAbsences(
          year,
          month,
          employees.map((e) => e.employeeId),
          employees.map((e) => e.companyId),
        );
      } catch (error) {
        notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
      } finally {
        setIsSubmitting(false);
        setPopupVisible(false);
      }
    },
    [
      selectedDate,
      selectedEndDate,
      userSend,
      users,
      user,
      absenceName,
      skipDaysOff,
      leaveForAssignment,
      notes,
      currentMonth,
      fetchAbsences,
      t,
      isHourlyAbsence,
      hourFrom,
      hourTo,
    ],
  );

  const handleCancel = useCallback(() => {
    if (viewMode === "AllEmployees") {
      fetchAllEmployeesData(currentMonth);
    }
    setPopupVisible(false);
    setUserSend([]);
    setCancelAbsenceData(undefined);
    setEditableAbsenceId(undefined);
  }, [viewMode, currentMonth, fetchAllEmployeesData]);

  const handleDeleteOwnAbsence = useCallback(
    async (id: number) => {
      const decision = await confirm(
        t("holidayPopup.areYouSureYouWantToCancelSelectedAbsence"),
        t("confirmationRequired"),
      );
      if (!decision) return;

      setIsSubmitting(true);
      try {
        const warning = await deleteOwnAbsence2(id);
        notify(t("holidayPopup.absenceHasBeenCanceled"), "success", 5000);
        if (warning) {
          notify(warning, "warning", 8000);
        }
        const empIds = [user?.employeeId ?? 0];
        const cmpIds = [user?.companyId ?? 0];
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        await fetchAbsences(year, month, empIds, cmpIds);
        if (viewMode === "AllEmployees") {
          await fetchAllEmployeesData(currentMonth);
        }
        setPopupVisible(false);
        setEditableAbsenceId(undefined);
      } catch (error) {
        notify((error as Error).message || t("Unknown error"), "error", 5000);
      } finally {
        setIsSubmitting(false);
      }
    },
    [t, user, currentMonth, fetchAbsences, fetchAllEmployeesData, viewMode],
  );

  const handleAbsenceCancel = useCallback(
    (id: number) => {
      confirm(
        t("holidayPopup.areYouSureYouWantToCancelSelectedAbsence"),
        t("confirmationRequired"),
      ).then((decision) => {
        if (decision === true) {
          setCancelRequestPending(true);
          cancelAbsence2(id)
            .then(() => {
              handleCancel();
              notify(t("holidayPopup.absenceHasBeenCanceled"), "success", 5000);
            })
            .catch((error: Error) => notify(error.message, "error", 5000))
            .finally(() => setCancelRequestPending(false));
        }
      });
    },
    [handleCancel, t],
  );

  return {
    popupVisible,
    setPopupVisible,
    selectedDate,
    setSelectedDate,
    selectedEndDate,
    setSelectedEndDate,
    absenceName,
    setAbsenceName,
    notes,
    setNotes,
    skipDaysOff,
    setSkipDaysOff,
    leaveForAssignment,
    setLeaveForAssignment,
    userSend,
    setUserSend,
    entriesEmployeeId,
    onlyWeekendDays,
    cancelAbsenceData,
    cancelRequestPending,
    isSubmitting,
    lastCellClickTime,
    editableAbsenceId,
    hourFrom,
    setHourFrom,
    hourTo,
    setHourTo,
    isHourlyAbsence,
    canBeHourly,
    addByHour,
    setAddByHour,
    handleDateClick,
    handleSubmitAbsence,
    handleCancel,
    handleAbsenceCancel,
    handleDeleteOwnAbsence,
  };
}
